"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.defineEventDeleted = exports.defineEventUpdated = exports.defineEventCreated = exports.defineQueryListUnbounded = exports.defineQueryLookup = exports.defineQueryAggregate = exports.defineQueryListBounded = exports.defineQueryGet = exports.defineCommandDelete = exports.defineCommandUpdate = exports.defineCommandCreate = void 0;
const contracts_core_1 = require("@omniflow/contracts-core");
/**
 * Typed define-helper'ы для CRUD-routes. Тонкая обёртка над
 * `defineCommand`/`defineQuery`/`defineEvent` из `@omniflow/contracts-core` с
 * двумя добавками:
 *
 * 1. **Template literal constraint на subject** — каждая операция требует свой
 *    префикс. Нельзя зарегистрировать `cmd.foo.update` через `defineCommandCreate`:
 *
 *      defineCommandCreate('cmd.foo.update', ...)  // ❌ TS error
 *
 * 2. **Compile-time проверка субджекта** через template literal type. Shape
 *    payload'а/reply (наличие `id`, `patch`, `ok`, `errorCode` и т.п.)
 *    проверяется **на runtime** через zod parse (а не на TS-уровне) — это
 *    прагматичный компромисс: zod 3 не даёт чистого способа constraint'ить
 *    `z.infer<S> extends X` без многословных error messages. Если автор
 *    забыл `id` или `patch` — runtime получит `validation_error` от bus'а при
 *    первом вызове. Помогает sanity-tests в реальном коде.
 *
 * Желаемая форма payload/reply каждой операции описана в JSDoc через ссылку
 * на abstract-shape (см. `abstract-shapes.ts`):
 *
 *   - `cmd.<e>.create`  → payload = entity fields without PlatformFields; reply = CrudCreateReply<TSnapshot>
 *   - `cmd.<e>.update`  → payload = CrudUpdateCommandShape<TSnapshot>;     reply = CrudUpdateReply
 *   - `cmd.<e>.delete`  → payload = CrudDeleteCommandShape;                reply = CrudDeleteReply
 *   - `q.<e>.get`       → payload = CrudGetQueryShape;                     reply = CrudGetReply<TSnapshot>
 *   - `q.<e>.list`      → payload = bounded/unbounded list query;          reply = CrudListReply<TSnapshot>
 *   - `evt.<e>.created` → payload = NamedPayload<ExtractEntity<S>, TSnapshot>
 *   - `evt.<e>.updated` → payload = NamedPayload<ExtractEntity<S>, TSnapshot>
 *   - `evt.<e>.deleted` → payload = CrudDeletedEventPayload (`{id, tenantId}`)
 */
// ── Commands ──
/** `cmd.<entity>.create` — body без id/tenantId; reply содержит item: TSnapshot. */
const defineCommandCreate = (subject, cmdSchema, replySchema) => (0, contracts_core_1.defineCommand)(subject, cmdSchema, replySchema);
exports.defineCommandCreate = defineCommandCreate;
/** `cmd.<entity>.update` — body `{id, patch}`; reply ack. */
const defineCommandUpdate = (subject, cmdSchema, replySchema) => (0, contracts_core_1.defineCommand)(subject, cmdSchema, replySchema);
exports.defineCommandUpdate = defineCommandUpdate;
/** `cmd.<entity>.delete` — body `{id}`; reply ack. */
const defineCommandDelete = (subject, cmdSchema, replySchema) => (0, contracts_core_1.defineCommand)(subject, cmdSchema, replySchema);
exports.defineCommandDelete = defineCommandDelete;
// ── Queries ──
/** `q.<entity>.get` — input `{id}`; reply discriminated found. */
const defineQueryGet = (subject, querySchema, replySchema) => (0, contracts_core_1.defineQuery)(subject, querySchema, replySchema);
exports.defineQueryGet = defineQueryGet;
/**
 * `q.<entity>.list` — bounded форма (limit/offset/sort optional, дефолт = всё).
 *
 * Для unbounded entity (CDR, audit, archived) использовать `defineQueryListUnbounded`.
 * Subject одинаковый (`q.<entity>.list`), различие только в shape query (см.
 * `boundedListQuery` vs `unboundedListQuery` в `reply-helpers.ts`).
 */
const defineQueryListBounded = (subject, querySchema, replySchema) => (0, contracts_core_1.defineQuery)(subject, querySchema, replySchema);
exports.defineQueryListBounded = defineQueryListBounded;
/**
 * `q.<entity>.aggregate` — агрегационный запрос (groupBy/aggr/having);
 * reply = CrudAggregateReply (groups + total). Query собирается из
 * `crudAggregateShape(...)` + `.superRefine(crudAggregateRefine)`.
 */
const defineQueryAggregate = (subject, querySchema, replySchema) => (0, contracts_core_1.defineQuery)(subject, querySchema, replySchema);
exports.defineQueryAggregate = defineQueryAggregate;
/**
 * `q.<entity>.lookup` — справочное чтение для выпадашек (эпик ACCESS): `id` + поля из
 * `defineEntity(..., { lookup })`, текстовый фильтр, потолок limit. Доступно каждому
 * вошедшему человеку тенанта, поэтому набор полей — осознанное решение автора сущности.
 */
const defineQueryLookup = (subject, querySchema, replySchema) => (0, contracts_core_1.defineQuery)(subject, querySchema, replySchema);
exports.defineQueryLookup = defineQueryLookup;
/** `q.<entity>.list` — unbounded форма (limit/offset/sort REQUIRED). */
const defineQueryListUnbounded = (subject, querySchema, replySchema) => (0, contracts_core_1.defineQuery)(subject, querySchema, replySchema);
exports.defineQueryListUnbounded = defineQueryListUnbounded;
// ── Events ──
/**
 * `evt.<entity>.created` — payload `{<entity>: TSnapshot}` через NamedPayload
 * mapped type. Извлечение entity-name из subject — `ExtractEntity<S>`.
 */
const defineEventCreated = (subject, payloadSchema) => (0, contracts_core_1.defineEvent)(subject, payloadSchema);
exports.defineEventCreated = defineEventCreated;
/**
 * `evt.<entity>.updated` — payload `{<entity>: TSnapshot}`.
 *
 * `previous` в wire НЕ передаём — DataSource SDK хранит previous в Map'е и
 * эмитит typed `update({previous, current})` подписчику локально. Каждый
 * subscriber-event имеет одинаковую форму, не optional branching.
 */
const defineEventUpdated = (subject, payloadSchema) => (0, contracts_core_1.defineEvent)(subject, payloadSchema);
exports.defineEventUpdated = defineEventUpdated;
/** `evt.<entity>.deleted` — payload `{id, tenantId}` (минимум для audit). */
const defineEventDeleted = (subject, payloadSchema) => (0, contracts_core_1.defineEvent)(subject, payloadSchema);
exports.defineEventDeleted = defineEventDeleted;
//# sourceMappingURL=define-crud.js.map